const ICMS_GRUPOS = ['ICMS00', 'ICMS90', 'ICMS40', 'ICMSSN'] as const;

export type IcmsGrupo = (typeof ICMS_GRUPOS)[number];

export function getIcmsGrupo(imposto: Record<string, any> | undefined): IcmsGrupo {
  if (!imposto) return 'ICMS00';
  return ICMS_GRUPOS.find((g) => imposto[g]) || 'ICMS00';
}

export function round2(value: number): number {
  return Number((Number(value) || 0).toFixed(2));
}

export function calcValorImposto(vBC: number, aliquota: number): number {
  return round2((vBC * (Number(aliquota) || 0)) / 100);
}

export function getAnoEmissao(dhEmi?: string | null): number {
  if (!dhEmi) return new Date().getFullYear();
  const d = new Date(dhEmi);
  return Number.isNaN(d.getTime()) ? new Date().getFullYear() : d.getFullYear();
}

export function getAliquotasIbsCbsPorAno(ano: number) {
  return {
    ibsUf: ano === 2026 ? 0.1 : ano <= 2028 ? 0.05 : 0.1,
    ibsMun: ano === 2026 ? 0.0 : ano <= 2028 ? 0.05 : 0.05,
    cbs: 0.9,
  };
}

export function calcularValorProduto(prod: any): number {
  const vItem = Number(prod?.vItem) || 0;
  const quantidade = Number(prod?.qFaturada) || 0;
  const vDesc = Number(prod?.vDesc) || 0;
  const vOutro = Number(prod?.vOutro) || 0;

  return round2(Math.max(0, vItem * quantidade - vDesc + vOutro));
}

function calcularGrupoComAliquota(grupo: any, vBC: number, campoAliquota: string, campoValor: string) {
  if (!grupo) return;
  grupo.vBC = vBC;
  grupo[campoValor] = calcValorImposto(vBC, Number(grupo[campoAliquota]) || 0);
}

function calcularIcmsItem(imposto: Record<string, any>, vBC: number) {
  const grupo = getIcmsGrupo(imposto);
  if (grupo === 'ICMS40') return;

  const icms = imposto[grupo];
  if (!icms) return;

  if (grupo === 'ICMSSN') return;

  icms.vBC = vBC;
  icms.vICMS = calcValorImposto(vBC, Number(icms.pICMS) || 0);
}

function calcularIbscbsItem(imposto: Record<string, any>, vBC: number, ano: number) {
  if (!imposto.IBSCBS) return;

  const defaults = getAliquotasIbsCbsPorAno(ano);
  if (!imposto.IBSCBS.gIBSCBS) imposto.IBSCBS.gIBSCBS = {};

  const g = imposto.IBSCBS.gIBSCBS;
  g.vBC = vBC;

  const pIBSUF = Number(g.gIBSUF?.pIBSUF ?? defaults.ibsUf);
  const pIBSMun = Number(g.gIBSMun?.pIBSMun ?? defaults.ibsMun);
  const pCBS = Number(g.gCBS?.pCBS ?? defaults.cbs);

  const vIBSUF = calcValorImposto(vBC, pIBSUF);
  const vIBSMun = calcValorImposto(vBC, pIBSMun);
  const vCBS = calcValorImposto(vBC, pCBS);

  g.gIBSUF = { ...(g.gIBSUF || {}), pIBSUF, vIBSUF };
  g.gIBSMun = { ...(g.gIBSMun || {}), pIBSMun, vIBSMun };
  g.vIBS = round2(vIBSUF + vIBSMun);
  g.gCBS = { ...(g.gCBS || {}), pCBS, vCBS };
}

function calcularImpostosItem(item: any, vBC: number, ano: number) {
  const imposto = item.imposto || {};

  calcularIcmsItem(imposto, vBC);
  calcularGrupoComAliquota(imposto.PIS, vBC, 'pPIS', 'vPIS');
  calcularGrupoComAliquota(imposto.COFINS, vBC, 'pCOFINS', 'vCOFINS');
  calcularGrupoComAliquota(imposto.FUST, vBC, 'pFUST', 'vFUST');
  calcularGrupoComAliquota(imposto.FUNTTEL, vBC, 'pFUNTTEL', 'vFUNTTEL');
  calcularIbscbsItem(imposto, vBC, ano);

  if (!imposto.retTrib) {
    imposto.retTrib = {
      vRetPIS: 0,
      vRetCofins: 0,
      vRetCSLL: 0,
      vBCIRRF: 0,
      vIRRF: 0,
    };
  }

  item.imposto = imposto;
}

function createEmptyTotal() {
  return {
    vProd: 0,
    ICMSTot: { vBC: 0, vICMS: 0, vICMSDeson: 0, vFCP: 0 },
    vCOFINS: 0,
    vPIS: 0,
    vFUNTTEL: 0,
    vFUST: 0,
    vRetTribTot: { vRetPIS: 0, vRetCofins: 0, vRetCSLL: 0, vIRRF: 0 },
    vDesc: 0,
    vOutro: 0,
    vNF: 0,
    IBSCBSTot: {
      vBCIBSCBS: 0,
      gIBS: {
        gIBSUF: { vDif: 0, vDevTrib: 0, vIBSUF: 0 },
        gIBSMun: { vDif: 0, vDevTrib: 0, vIBSMun: 0 },
        vIBS: 0,
      },
      gCBS: { vDif: 0, vDevTrib: 0, vCBS: 0 },
    },
    vTotDFe: 0,
  };
}

function acumularTotal(acc: ReturnType<typeof createEmptyTotal>, item: any) {
  const prod = item.prod || {};
  const imp = item.imposto || {};
  const vProd = Number(prod.vProd) || 0;
  const vDesc = Number(prod.vDesc) || 0;
  const vOutro = Number(prod.vOutro) || 0;

  acc.vProd = round2(acc.vProd + vProd);
  acc.vDesc = round2(acc.vDesc + vDesc);
  acc.vOutro = round2(acc.vOutro + vOutro);
  acc.vNF = round2(acc.vNF + vProd);

  const icmsGrupo = getIcmsGrupo(imp);
  const icms = imp[icmsGrupo];
  if (icms && icmsGrupo !== 'ICMS40' && icmsGrupo !== 'ICMSSN') {
    acc.ICMSTot.vBC = round2(acc.ICMSTot.vBC + (Number(icms.vBC) || 0));
    acc.ICMSTot.vICMS = round2(acc.ICMSTot.vICMS + (Number(icms.vICMS) || 0));
  }

  acc.vCOFINS = round2(acc.vCOFINS + (Number(imp.COFINS?.vCOFINS) || 0));
  acc.vPIS = round2(acc.vPIS + (Number(imp.PIS?.vPIS) || 0));
  acc.vFUNTTEL = round2(acc.vFUNTTEL + (Number(imp.FUNTTEL?.vFUNTTEL) || 0));
  acc.vFUST = round2(acc.vFUST + (Number(imp.FUST?.vFUST) || 0));

  const ret = imp.retTrib || {};
  acc.vRetTribTot.vRetPIS = round2(acc.vRetTribTot.vRetPIS + (Number(ret.vRetPIS) || 0));
  acc.vRetTribTot.vRetCofins = round2(acc.vRetTribTot.vRetCofins + (Number(ret.vRetCofins) || 0));
  acc.vRetTribTot.vRetCSLL = round2(acc.vRetTribTot.vRetCSLL + (Number(ret.vRetCSLL) || 0));
  acc.vRetTribTot.vIRRF = round2(acc.vRetTribTot.vIRRF + (Number(ret.vIRRF) || 0));

  const ibscbs = imp.IBSCBS?.gIBSCBS;
  if (ibscbs) {
    acc.IBSCBSTot.vBCIBSCBS = round2(acc.IBSCBSTot.vBCIBSCBS + (Number(ibscbs.vBC) || 0));
    acc.IBSCBSTot.gIBS.gIBSUF.vIBSUF = round2(
      acc.IBSCBSTot.gIBS.gIBSUF.vIBSUF + (Number(ibscbs.gIBSUF?.vIBSUF) || 0)
    );
    acc.IBSCBSTot.gIBS.gIBSMun.vIBSMun = round2(
      acc.IBSCBSTot.gIBS.gIBSMun.vIBSMun + (Number(ibscbs.gIBSMun?.vIBSMun) || 0)
    );
    acc.IBSCBSTot.gIBS.vIBS = round2(acc.IBSCBSTot.gIBS.vIBS + (Number(ibscbs.vIBS) || 0));
    acc.IBSCBSTot.gCBS.vCBS = round2(acc.IBSCBSTot.gCBS.vCBS + (Number(ibscbs.gCBS?.vCBS) || 0));
  }
}

/** Recalcula produtos, impostos por item e totais da NFCom. */
export function recalcularNfcomDados<T extends { infNFCom?: any }>(dados: T): T {
  const out = JSON.parse(JSON.stringify(dados)) as T;
  const inf = out.infNFCom;
  if (!inf?.det || !Array.isArray(inf.det)) return out;

  const ano = getAnoEmissao(inf.ide?.dhEmi);
  const totalAcc = createEmptyTotal();

  inf.det.forEach((item: any) => {
    if (!item.prod) item.prod = {};
    const vProd = calcularValorProduto(item.prod);
    item.prod.vProd = vProd;

    const vBC = vProd;
    calcularImpostosItem(item, vBC, ano);
    acumularTotal(totalAcc, item);
  });

  const temIbscbs = inf.det.some((item: any) => item.imposto?.IBSCBS?.gIBSCBS);
  const totalAnterior = inf.total || {};

  inf.total = {
    ...totalAcc,
    ICMSTot: {
      vBC: totalAcc.ICMSTot.vBC,
      vICMS: totalAcc.ICMSTot.vICMS,
      vICMSDeson: Number(totalAnterior.ICMSTot?.vICMSDeson) || 0,
      vFCP: Number(totalAnterior.ICMSTot?.vFCP) || 0,
    },
    ...(temIbscbs
      ? {
          IBSCBSTot: totalAcc.IBSCBSTot,
          vTotDFe:
            ano === 2026
              ? totalAcc.vNF
              : round2(totalAcc.vNF + totalAcc.IBSCBSTot.gIBS.vIBS + totalAcc.IBSCBSTot.gCBS.vCBS),
        }
      : {
          IBSCBSTot: undefined,
          vTotDFe: undefined,
        }),
  };

  if (!temIbscbs) {
    delete inf.total.IBSCBSTot;
    delete inf.total.vTotDFe;
  }

  return out;
}

/** Campos editáveis que disparam recálculo automático. */
export function deveRecalcularNfcom(path: string): boolean {
  if (/^infNFCom\.det\.\d+\.prod\.(qFaturada|vItem|vProd|vDesc|vOutro)$/.test(path)) return true;
  if (/^infNFCom\.det\.\d+\.imposto\./.test(path) && /\.p[A-Z]/.test(path)) return true;
  if (/^infNFCom\.det\.\d+\.imposto\.IBSCBS\.(CST|cClassTrib)$/.test(path)) return true;
  if (/^infNFCom\.det\.\d+\.imposto\..*\.CST$/.test(path)) return true;
  return false;
}

export function isCampoCalculadoNfcom(path: string): boolean {
  if (path === 'infNFCom.total' || path.startsWith('infNFCom.total.')) return true;
  if (/\.prod\.vProd$/.test(path)) return true;
  if (/\.imposto\.(ICMS00|ICMS90)\.(vBC|vICMS)$/.test(path)) return true;
  if (/\.imposto\.(PIS|COFINS|FUST|FUNTTEL)\.vBC$/.test(path)) return true;
  if (/\.imposto\.PIS\.vPIS$/.test(path)) return true;
  if (/\.imposto\.COFINS\.vCOFINS$/.test(path)) return true;
  if (/\.imposto\.FUST\.vFUST$/.test(path)) return true;
  if (/\.imposto\.FUNTTEL\.vFUNTTEL$/.test(path)) return true;
  if (/\.imposto\.retTrib\./.test(path)) return true;
  if (/\.imposto\.IBSCBS\.gIBSCBS\.(vBC|vIBS)$/.test(path)) return true;
  if (/\.imposto\.IBSCBS\.gIBSCBS\.g(IBSUF|IBSMun|CBS)\.v/.test(path)) return true;
  return false;
}
